import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@porchpass/ui";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAddEmployeeModal, useFeatureFlags } from "src/context";
import { useDgaModal } from "src/context/DgaModalContext";
import { useDealerUserInfo } from "src/context/DealerUserInfoContext";
import { useDealer } from "src/hooks/useDealer";

const PlusIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="13.5"
    height="13.5"
    viewBox="0 0 16.125 16.125"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="transition-transform duration-200 ease-in-out shrink-0"
    style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0deg)" }}
  >
    <path
      d="M8.0625 0C12.5153 0 16.125 3.6097 16.125 8.0625C16.125 12.5153 12.5153 16.125 8.0625 16.125C3.6097 16.125 0 12.5153 0 8.0625C0 3.6097 3.6097 0 8.0625 0ZM8.0625 4.3125C7.64829 4.3125 7.3125 4.64829 7.3125 5.0625V7.3125H5.0625C4.64829 7.3125 4.3125 7.64829 4.3125 8.0625C4.3125 8.47671 4.64829 8.8125 5.0625 8.8125H7.3125V11.0625C7.3125 11.4767 7.64829 11.8125 8.0625 11.8125C8.47671 11.8125 8.8125 11.4767 8.8125 11.0625V8.8125H11.0625C11.4767 8.8125 11.8125 8.47671 11.8125 8.0625C11.8125 7.64829 11.4767 7.3125 11.0625 7.3125H8.8125V5.0625C8.8125 4.64829 8.47671 4.3125 8.0625 4.3125Z"
      fill="currentColor"
    />
  </svg>
);

const CreateApplicationIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    className="shrink-0"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.5 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V6L10.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.5 1.5V6H15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 13.5V9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.75 11.25H11.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InviteToApplyIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    className="shrink-0"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.75 2.25V6.75H11.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.25 9C2.25 10.3924 2.80312 11.7277 3.78769 12.7123C4.77226 13.6969 6.10761 14.25 7.5 14.25C8.89239 14.25 10.2277 13.6969 11.2123 12.7123C12.1969 11.7277 12.75 10.3924 12.75 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.25 15.75V11.25H6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.75 9C15.75 7.60761 15.1969 6.27226 14.2123 5.28769C13.2277 4.30312 11.8924 3.75 10.5 3.75C9.10761 3.75 7.77226 4.30312 6.78769 5.28769C5.80312 6.27226 5.25 7.60761 5.25 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AddEmployeeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    className="shrink-0"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 15.75V14.25C12 13.4544 11.6839 12.6913 11.1213 12.1287C10.5587 11.5661 9.79565 11.25 9 11.25H4.5C3.70435 11.25 2.94129 11.5661 2.37868 12.1287C1.81607 12.6913 1.5 13.4544 1.5 14.25V15.75M15 6V10.5M17.25 8.25H12.75M9.375 5.25C9.375 6.90685 8.03185 8.25 6.375 8.25C4.71815 8.25 3.375 6.90685 3.375 5.25C3.375 3.59315 4.71815 2.25 6.375 2.25C8.03185 2.25 9.375 3.59315 9.375 5.25Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MenuItemWithShortcut = ({
  icon,
  children,
  shortcut,
  onClick,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  shortcut?: string;
  onClick?: () => void;
}) => (
  <DropdownMenuItem className="justify-between" onSelect={onClick}>
    <span className="flex flex-1 gap-4 items-center min-w-0 whitespace-nowrap">
      {icon}
      {children}
    </span>
    {shortcut && (
      <span className="ml-4 shrink-0 text-[9px] font-medium text-[#4a5466] uppercase leading-[13px]">
        {shortcut}
      </span>
    )}
  </DropdownMenuItem>
);

export default function NewButton() {
  const navigate = useNavigate();
  const { dealer_id } = useDealer();
  const { openAddEmployeeModal } = useAddEmployeeModal();
  const { openDgaModal } = useDgaModal();
  const { isDealerGeneratedAppEnabled } = useFeatureFlags();
  const [open, setOpen] = useState(false);
  const { dealerUserInfo } = useDealerUserInfo();
  const canManageEmployees =
    dealerUserInfo.is_admin || dealerUserInfo.role === "GENERAL_MANAGER";

  const handleCreateApplication = () => {
    openDgaModal();
  };

  const handleInviteToApply = () => {
    void navigate(`/${dealer_id}/invite`);
  };

  const handleAddEmployee = () => {
    openAddEmployeeModal();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="dark"
          size="s"
          className="pr-3 pl-4 w-full h-10"
          innerClassName="justify-between"
        >
          <span className="flex gap-3 items-center">
            <PlusIcon isOpen={open} />
            <span>New</span>
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] mt-1"
      >
        {isDealerGeneratedAppEnabled && (
          <MenuItemWithShortcut
            icon={<CreateApplicationIcon />}
            // shortcut="Shift+C"
            onClick={handleCreateApplication}
          >
            Create application
          </MenuItemWithShortcut>
        )}
        <MenuItemWithShortcut
          icon={<InviteToApplyIcon />}
          // shortcut="Shift+I"
          onClick={handleInviteToApply}
        >
          Invite to apply
        </MenuItemWithShortcut>
        {canManageEmployees && (
          <MenuItemWithShortcut
            icon={<AddEmployeeIcon />}
            // shortcut="Shift+E"
            onClick={handleAddEmployee}
          >
            Add employee
          </MenuItemWithShortcut>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
